import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, withLatestFrom } from 'rxjs';
import {
  Chart,
  type ChartOptions,
} from 'chart.js/auto';
import { getRelativePosition } from 'chart.js/helpers';
import { DatePipe } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';

import { selectCurrentSite } from '../../../features/site/site.selectors';
import { selectCurrentCut } from '../../../features/cut/cut.selectors';
import { selectUser } from '../../../features/user/user.selectors';
import { selectCurrentReferenceBatchId } from '../../../features/batch/batch.selectors';

import {
  loadCutProfiles,
  loadCutProfilesFailure,
  loadCutProfilesSuccess,
} from '../../../features/cut/cut.actions';

import { Cut, CutBatchData } from '../../../models/cut';
import { Site } from '../../../models/site';
import { User } from '../../../models/user';
import { Color } from '../../../enums/color';

import { CutChartView, XY } from './cut-chart.view';
import { CutChartInteractions } from './cut-chart.interactions';

@Component({
    selector: 'cut-chart',
    template: `
    <div #host class="chart-host">
      <canvas #canvas></canvas>
    </div>
  `,
    styles: [`
    :host {
      display: block;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }

    .chart-host {
      position: relative;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
    }

    canvas {
      display: block;
      max-width: 100%;
      max-height: 100%;
    }
  `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CutChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('host') hostRef?: ElementRef<HTMLElement>;

  @Input() canEditSlope = false;
  @Input() enableZoom = true;

  @Output() slopeDragging = new EventEmitter<XY[]>();
  @Output() slopeHasChanged = new EventEmitter<XY[]>();

  private chart?: Chart;
  private view?: CutChartView;
  private interactions?: CutChartInteractions;
  private ro?: ResizeObserver;
  private layoutRaf: number | null = null;

  private site?: Site | null;
  private cut?: Cut | null;
  private referenceBatchId?: string | null;
  private user?: User | null;

  private slope: XY[] = [];

  private onMouseDownNative = (e: MouseEvent) => this.interactions?.onMouseDown(e);
  private onMouseMoveNative = (e: MouseEvent) => this.interactions?.onMouseMove(e);
  private onMouseUpNative = (e: MouseEvent) => this.interactions?.onMouseUp(e);
  private onKeyDownNative = (e: KeyboardEvent) => this.interactions?.onKeyDown(e);

  constructor(
    private store: Store,
    private actions: Actions,
    private translocoService: TranslocoService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.actions
      .pipe(ofType(loadCutProfilesSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        if (result?.profiles && this.site && this.cut && this.user) {
          this.updateChart(
            this.site,
            this.cut,
            this.user,
            result.profiles,
            this.referenceBatchId,
          );
        }
      });

    this.actions
      .pipe(ofType(loadCutProfilesFailure), takeUntilDestroyed())
      .subscribe(() => this.clearChart());

    combineLatest([
      this.store.select(selectCurrentCut),
      this.store.select(selectCurrentReferenceBatchId),
    ])
      .pipe(
        withLatestFrom(
          this.store.select(selectCurrentSite),
          this.store.select(selectUser),
        ),
        takeUntilDestroyed()
      )
      .subscribe(([
                    [cut, referenceBatchId],
                    site,
                    user
                  ]) => {
        this.site = site;
        this.cut = cut;
        this.referenceBatchId = referenceBatchId;
        this.user = user;

        this.refreshSlopeEditability();

        if (this.site && this.cut) {
          this.store.dispatch(
            loadCutProfiles({
              organizationId: this.site.organization,
              siteId: this.site.id,
              cutId: this.cut.id,
            }),
          );
        }
      });
  }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.chart = new Chart(ctx, {
      type: 'scatter',
      data: { datasets: [] },
      options: this.buildBaseOptions(),
    });

    this.view = new CutChartView(
      this.chart,
      (data, editable) => this.makeSlopeDataset(data, editable),
    );

    this.interactions = new CutChartInteractions(
      canvas,
      this.view,
      () => {
        const user = this.user;
        const cut = this.cut;
        return !!(this.canEditSlope && user && cut && user.name === cut.author);
      },
      () => {
        const user = this.user;
        const cut = this.cut;
        return !!(this.canEditSlope && user && cut && user.name === cut.author);
      },
      (ev) => this.eventToValues(ev),
      (slope) => this.zone.run(() => this.slopeDragging.emit(slope)),
      (slope) => this.zone.run(() => this.slopeHasChanged.emit(slope)),
    );

    this.interactions.init();
    this.interactions.setZoomGloballyEnabled(this.enableZoom);

    this.zone.runOutsideAngular(() => {
      window.addEventListener('mouseup', this.onMouseUpNative, { passive: true });
      window.addEventListener('keydown', this.onKeyDownNative, { passive: true });

      canvas.addEventListener('mousedown', this.onMouseDownNative, { passive: true });
      canvas.addEventListener('mousemove', this.onMouseMoveNative, { passive: true });

      canvas.addEventListener(
        'contextmenu',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        { passive: false },
      );
    });

    const host = this.hostRef?.nativeElement;
    if (host) {
      let resizeRaf: number | null = null;

      this.ro = new ResizeObserver(() => {
        if (resizeRaf !== null) {
          cancelAnimationFrame(resizeRaf);
        }

        resizeRaf = requestAnimationFrame(() => {
          resizeRaf = null;
          this.scheduleChartLayout();
        });
      });

      this.ro.observe(host);
    }

    this.zone.runOutsideAngular(() => {
      this.scheduleChartLayout();
    });

    this.zone.run(() => this.cdr.markForCheck());
  }

  ngOnChanges(): void {
    if (!this.chart) return;

    this.chart.options = this.buildBaseOptions();
    this.chart.update('none');

    this.interactions?.setZoomGloballyEnabled(this.enableZoom);
    this.refreshSlopeEditability();

    if (!this.canEditSlope) {
      this.interactions?.cancelInteraction();
    }

    this.zone.runOutsideAngular(() => {
      this.scheduleChartLayout();
    });
  }

  ngOnDestroy(): void {
    const canvas = this.canvasRef?.nativeElement;

    if (canvas) {
      canvas.removeEventListener('mousedown', this.onMouseDownNative);
      canvas.removeEventListener('mousemove', this.onMouseMoveNative);
    }

    window.removeEventListener('mouseup', this.onMouseUpNative);
    window.removeEventListener('keydown', this.onKeyDownNative);

    if (this.layoutRaf !== null) {
      cancelAnimationFrame(this.layoutRaf);
    }

    this.ro?.disconnect();
    this.interactions?.destroy();
    this.chart?.destroy();
  }

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.interactions?.cancelInteraction();
    }
  }

  startDrawingSlope(): void {
    if (!this.canEditSlope) return;
    this.interactions?.armDrawSlope();
  }

  toggleDataset(datasetIndex: number): void {
    this.view?.toggleDataset(datasetIndex);
    this.scheduleChartLayout();
  }

  setChartDatasetVisible(datasetIndex: number, visible: boolean): void {
    this.view?.setDatasetVisible(datasetIndex, visible);
    this.scheduleChartLayout();
  }

  canvasToImage(filename: string): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    this.view?.canvasToImage(canvas, filename);
  }

  get datasets(): any[] {
    return this.view?.getDatasets() ?? [];
  }

  get hasChart(): boolean {
    return !!this.chart;
  }

  get isBusy(): boolean {
    return this.interactions?.isBusy ?? false;
  }

  get canStartDrawingSlope(): boolean {
    return this.canEditSlope && !this.isBusy;
  }

  public forceResize(): void {
    this.scheduleChartLayout();
  }

  private scheduleChartLayout(): void {
    if (!this.chart || !this.view) return;

    if (this.layoutRaf !== null) {
      cancelAnimationFrame(this.layoutRaf);
    }

    this.layoutRaf = requestAnimationFrame(() => {
      this.layoutRaf = null;

      this.view?.resize();

      this.chart?.update('none');

      this.applyEqualAxisScale();

      this.chart?.update('none');
    });
  }

  private buildBaseOptions(): ChartOptions {
    const base: any = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: { type: 'linear' },
        y: { type: 'linear' },
      },
    };

    if (this.enableZoom) {
      base.plugins.zoom = {
        pan: { enabled: true, mode: 'xy' },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'xy',
        },
      };
    }

    return base as ChartOptions;
  }

  private updateChart(
    site: Site,
    cut: Cut,
    user: User,
    profiles: Array<CutBatchData>,
    referenceBatchId?: string | null,
  ): void {
    if (!this.chart) return;

    this.slope = CutChartComponent.toXYArray((cut as any).slope);
    this.interactions?.setSlopeFromOutside(this.slope);

    const dateFormat = user.languageCode === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
    const datePipe = new DatePipe(this.translocoService.getActiveLang());

    const allowedBatches = new Set<string>();
    if (site.lastBatch) allowedBatches.add(site.lastBatch);
    if (referenceBatchId) allowedBatches.add(referenceBatchId);

    const filteredProfiles = profiles
      .filter((p) => !!p?.batch && allowedBatches.has(p.batch))
      .sort((a, b) => {
        const aIsLast = a.batch === site.lastBatch ? 0 : 1;
        const bIsLast = b.batch === site.lastBatch ? 0 : 1;
        return aIsLast - bIsLast;
      });

    const datasets: any[] = filteredProfiles.map((profile: CutBatchData) => {
      const isLast = profile.batch === site.lastBatch;
      const color = isLast ? (Color.PRIMARY as any) : (Color.ACCENT as any);

      return {
        type: 'scatter' as const,
        label: datePipe.transform(new Date(profile.dateAcquired), dateFormat) ?? '',
        data: (profile.dataset ?? []).map(([x, y]: any) => ({
          x: Number(x),
          y: Number(y),
        })),
        parsing: false,
        normalized: true,
        pointRadius: 1,
        pointHoverRadius: 1,
        borderColor: color,
        backgroundColor: color,
      };
    });

    const editable = this.canEditSlope && user?.name === cut.author;

    const liveSlope = this.interactions?.isDrawing
      ? this.interactions.getSlope()
      : this.slope;

    if (Array.isArray(liveSlope) && liveSlope.length === 2) {
      datasets.unshift(this.makeSlopeDataset(liveSlope, editable));
    }

    this.chart.data.datasets = datasets as any;
    this.chart.options = this.buildBaseOptions();

    this.zone.runOutsideAngular(() => {
      this.scheduleChartLayout();
    });
  }

  private makeSlopeDataset(data: XY[], editable: boolean) {
    const color = Color.DARK_GREY;
    const r = editable ? 6 : 0;

    return {
      type: 'line' as const,
      label: this.translocoService.translate('slope'),
      hidden: false,
      data: data.map(([x, y]) => ({ x, y })),
      parsing: false as const,
      normalized: true,
      showLine: true,
      spanGaps: true,
      borderWidth: 2,
      fill: false,
      borderColor: color as any,
      backgroundColor: color as any,
      pointRadius: r,
      pointHitRadius: editable ? 8 : 0,
      pointHoverRadius: editable ? 8 : 0,
      pointBackgroundColor: color as any,
    };
  }

  private clearChart(): void {
    this.view?.clear();
    this.slope = [];

    this.zone.runOutsideAngular(() => {
      this.scheduleChartLayout();
    });
  }

  private eventToValues(event: MouseEvent): { xValue: number; yValue: number } {
    if (!this.chart) {
      return { xValue: 0, yValue: 0 };
    }

    const pos = getRelativePosition(event as any, this.chart as any);

    const xScale = this.chart.scales['x'];
    const yScale = this.chart.scales['y'];

    if (!xScale || !yScale) {
      return { xValue: 0, yValue: 0 };
    }

    const x = xScale.getValueForPixel(pos.x);
    const y = yScale.getValueForPixel(pos.y);

    return {
      xValue: (x ?? xScale.min ?? 0) as number,
      yValue: (y ?? yScale.min ?? 0) as number,
    };
  }

  private static toXYArray(raw: unknown): XY[] {
    if (!Array.isArray(raw)) return [];

    const out: XY[] = [];

    for (const item of raw) {
      if (!Array.isArray(item) || item.length < 2) continue;

      const x = Number(item[0]);
      const y = Number(item[1]);

      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

      out.push([x, y]);

      if (out.length === 2) break;
    }

    return out;
  }

  private refreshSlopeEditability(): void {
    if (!this.chart || !this.view || !this.cut || !this.user) return;

    const editable = this.canEditSlope && this.user.name === this.cut.author;

    const ds = this.view.findSlopeDataset();
    if (!ds) return;

    const r = editable ? 6 : 0;

    (ds as any).pointRadius = r;
    (ds as any).pointHitRadius = editable ? 8 : 0;
    (ds as any).pointHoverRadius = editable ? 8 : 0;
    (ds as any).hidden = false;

    this.chart.update('none');
  }

  private applyEqualAxisScale(): void {
    if (!this.chart) return;

    const chart = this.chart;
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];
    const area = chart.chartArea;

    if (!xScale || !yScale || !area) return;

    const visibleDatasets = chart.data.datasets.filter(
      (ds: any) => !(ds.hidden ?? false),
    );

    const points: Array<{ x: number; y: number }> = [];

    for (const ds of visibleDatasets as any[]) {
      for (const p of ds.data ?? []) {
        if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
          points.push({ x: Number(p.x), y: Number(p.y) });
        }
      }
    }

    if (!points.length) return;

    const minX = Math.min(...points.map((p) => p.x));
    const maxX = Math.max(...points.map((p) => p.x));
    const minY = Math.min(...points.map((p) => p.y));
    const maxY = Math.max(...points.map((p) => p.y));

    const width = area.right - area.left;
    const height = area.bottom - area.top;

    if (width <= 0 || height <= 0) return;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const rangeX = Math.max(maxX - minX, 1e-6);
    const rangeY = Math.max(maxY - minY, 1e-6);

    const pixelRatio = width / height;

    let finalRangeX = rangeX;
    let finalRangeY = rangeY;

    if (rangeX / rangeY > pixelRatio) {
      finalRangeY = rangeX / pixelRatio;
    } else {
      finalRangeX = rangeY * pixelRatio;
    }

    xScale.options.min = centerX - finalRangeX / 2;
    xScale.options.max = centerX + finalRangeX / 2;
    yScale.options.min = centerY - finalRangeY / 2;
    yScale.options.max = centerY + finalRangeY / 2;
  }
}
